import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { FulfillmentStage, OrderCreatedResponse } from '../../shared/types';
import { Badge, Button, Card, LoadingScreen, cx } from '../components/Ui';
import { usePublicOrderStatus } from '../hooks/businessData';
import { useTenant } from '../contexts/TenantContext';
import { formatLocalMinor, formatUsd } from '../lib/currency';

const copy = {
  pendiente: ['Pedido recibido', 'El negocio debe confirmar y pasar tu pedido a cocina.', 'amber'],
  preparacion: ['En preparación', 'La cocina está preparando tu pedido.', 'blue'],
  completado: ['Pedido completado', 'Tu pedido está listo o fue entregado.', 'green'],
  cancelado: ['Pedido cancelado', 'Comunícate con el negocio si necesitas más información.', 'red'],
  fantasma: ['Pedido cerrado', 'El negocio no pudo confirmar el contacto asociado al pedido.', 'purple'],
} as const;

const fulfillmentCopy: Record<FulfillmentStage, { title: string; description: string; tone: 'amber' | 'blue' | 'green' | 'red' | 'slate' }> = {
  kitchen: { title: 'En cocina', description: 'La cocina está preparando tu pedido.', tone: 'blue' },
  delivery_prep: { title: 'En preparación', description: 'Estamos preparando tu pedido para el reparto.', tone: 'blue' },
  ready: { title: 'Pedido listo', description: 'La cocina terminó. El negocio lo mantiene listo para entrega, retiro o despacho.', tone: 'green' },
  transit: { title: 'En reparto', description: 'Tu pedido ya salió para entrega.', tone: 'blue' },
  delivered: { title: 'Pedido entregado', description: 'El negocio registró la entrega del pedido.', tone: 'green' },
  handed_off: { title: 'Pedido retirado o entregado en local', description: 'El negocio registró la entrega directa del pedido.', tone: 'green' },
  cancelled: { title: 'Pedido cancelado', description: 'El negocio canceló el pedido. Comunícate si necesitas más información.', tone: 'red' },
};

const timelineSteps = [
  { key: 'received', label: 'Recibido' },
  { key: 'kitchen', label: 'En cocina' },
  { key: 'delivery_prep', label: 'En preparación de reparto' },
  { key: 'ready', label: 'Listo' },
  { key: 'transit', label: 'En reparto' },
  { key: 'delivered', label: 'Entregado' },
] as const;

function activeTimelineKey(order: { status: keyof typeof copy; fulfillmentStage: FulfillmentStage | null }) {
  if (order.status === 'cancelado' || order.fulfillmentStage === 'cancelled') return 'cancelled';
  if (order.fulfillmentStage === 'handed_off') return 'handed_off';
  if (order.fulfillmentStage === 'delivered') return 'delivered';
  if (order.fulfillmentStage === 'transit') return 'transit';
  if (order.fulfillmentStage === 'ready') return 'ready';
  if (order.fulfillmentStage === 'delivery_prep') return 'delivery_prep';
  if (order.status === 'preparacion' || order.fulfillmentStage === 'kitchen') return 'kitchen';
  return 'received';
}

export default function OrderStatusPage() {
  const { token = '' } = useParams();
  const location = useLocation();
  const { tenant } = useTenant();
  const { data: order, isLoading, error } = usePublicOrderStatus(token);
  const whatsappUrl = useMemo(() => {
    const state = location.state as Partial<OrderCreatedResponse> | null;
    const candidate = state?.whatsappUrl || sessionStorage.getItem(`menucloud.order.whatsapp.${token}`);
    if (!candidate) return null;
    try {
      const parsed = new URL(candidate);
      return parsed.protocol === 'https:' && parsed.hostname === 'wa.me' ? candidate : null;
    } catch {
      return null;
    }
  }, [location.state, token]);

  if (isLoading) return <LoadingScreen label="Consultando el pedido…" />;
  if (!order) return <main className="grid min-h-screen place-items-center bg-slate-100 p-5"><Card className="max-w-md text-center"><h1 className="text-xl font-black">Pedido no encontrado</h1><p className="mt-2 text-sm text-slate-500">{error instanceof Error ? error.message : ''}</p><Link to="/"><Button className="mt-5">Volver al menú</Button></Link></Card></main>;
  const fallback = copy[order.status];
  const stageCopy = order.fulfillmentStage ? fulfillmentCopy[order.fulfillmentStage] : null;
  const title = stageCopy?.title ?? fallback[0];
  const description = stageCopy?.description ?? fallback[1];
  const tone = stageCopy?.tone ?? fallback[2];
  const activeStep = activeTimelineKey(order);

  return (
    <main className="grid min-h-screen place-items-center bg-slate-100 p-4">
      <Card className="w-full max-w-lg p-7 text-center sm:p-9">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-950 text-3xl text-white">#{String(order.correlation).padStart(2, '0')}</div>
        <div className="mt-5"><Badge tone={tone}>{order.status}</Badge></div>
        <h1 className="mt-3 text-3xl font-black text-slate-950">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
        <ol className="mt-6 grid gap-2 text-left" aria-label="Estado del pedido">
          {activeStep === 'cancelled' ? (
            <li className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-800">Cancelado: el pedido fue cancelado por el negocio.</li>
          ) : activeStep === 'handed_off' ? (
            <>
              {timelineSteps.slice(0, 3).map((step) => <li key={step.key} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800">Completado: {step.label}</li>)}
              <li className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800">Completado: Retirado o entregado en local</li>
            </>
          ) : (
            timelineSteps.map((step) => {
              const orderIndex = timelineSteps.findIndex((item) => item.key === activeStep);
              const stepIndex = timelineSteps.findIndex((item) => item.key === step.key);
              const done = stepIndex < orderIndex;
              const current = step.key === activeStep;
              return <li key={step.key} className={cx('rounded-xl border p-3 text-sm', done ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : current ? 'border-slate-900 bg-slate-950 text-white' : 'border-slate-200 bg-white text-slate-500')}><span className="font-bold">{done ? 'Completado' : current ? 'Actual' : 'Pendiente'}:</span> {step.label}</li>;
            })
          )}
        </ol>
        <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-left">
          <div className="flex justify-between text-sm"><span>Total USD</span><strong>{formatUsd(order.totalUsdCents)}</strong></div>
          <div className="mt-2 flex justify-between text-sm"><span>Total local</span><strong>{formatLocalMinor(order.totalLocalMinor, order.localCurrency, order.currencyDecimals, order.currencySymbol)}</strong></div>
          <div className="mt-2 flex justify-between text-xs text-slate-500"><span>Actualizado</span><span>{new Date(order.updatedAt).toLocaleString()}</span></div>
        </div>
        <p className="mt-5 text-xs text-slate-400">Esta pantalla se actualiza automáticamente mientras el pedido sigue activo.</p>
        <div className="mt-6 grid gap-3">
          {whatsappUrl && (
            <Button onClick={() => window.open(whatsappUrl, '_blank', 'noopener,noreferrer')}>Consultar sobre mi pedido</Button>
          )}
          <Link to="/"><Button variant="secondary" className="w-full">Volver a {tenant?.name || 'menú'}</Button></Link>
        </div>
      </Card>
    </main>
  );
}
